// src/components/BlockRenderer.jsx
import React from 'react';
import PropTypes from 'prop-types';
import './BlockRenderer.css';

function BlockRenderer({
  block,
  isSelected,
  onSelect,
  onDelete,
  onDragStart,
  onDrop,
  onDragOver,
}) {
  let content = '';

  // Helper function to get text styling
  const getTextStyle = (block) => {
    // Add fallback fonts for better compatibility
    const getFontFamilyWithFallback = (fontFamily) => {
      if (!fontFamily) return undefined;

      const fallbacks = {
        Marianne: '"Marianne", Arial, sans-serif',
        Arial: 'Arial, sans-serif',
        Helvetica: 'Helvetica, Arial, sans-serif',
        Georgia: 'Georgia, serif',
        'Times New Roman': '"Times New Roman", Times, serif',
        'Courier New': '"Courier New", Courier, monospace',
        Verdana: 'Verdana, sans-serif',
        Roboto: 'Roboto, Arial, sans-serif',
      };

      return fallbacks[fontFamily] || `"${fontFamily}", sans-serif`;
    };

    return {
      fontSize: block.fontSize ? `${block.fontSize}px` : undefined,
      fontFamily: getFontFamilyWithFallback(block.fontFamily),
      fontWeight: block.fontWeight || undefined,
      fontStyle: block.fontStyle || undefined,
      textDecoration: block.textDecoration || undefined,
      textAlign: block.textAlign || undefined,
    };
  };

  let tag;
  if (block.type === 'heading') {
    tag = block.level || 'h2';
  }

  switch (block.type) {
    case 'heading':
      content = React.createElement(
        tag,
        { style: getTextStyle(block) },
        block.content
      );
      break;

    case 'paragraph':
      content = <p style={getTextStyle(block)}>{block.content}</p>;
      break;

    case 'richtext':
      content = (
        <div
          style={getTextStyle(block)}
          dangerouslySetInnerHTML={{ __html: block.content }}
        />
      );
      break;

    case 'features':
      content = (
        <ul>
          {(block.items || []).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
      break;

    case 'stats':
      content = (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '20px',
            margin: '20px 0',
          }}
        >
          {(block.items || []).map((stat, i) => (
            <div
              key={i}
              style={{
                textAlign: 'center',
                padding: '20px',
                background: '#f5f3f0',
                borderRadius: '4px',
              }}
            >
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#8b0000',
                  marginBottom: '8px',
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  fontSize: '0.9rem',
                  color: '#666',
                  textTransform: 'uppercase',
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      );
      break;

    case 'contact':
      content = (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            margin: '20px 0',
          }}
        >
          {(block.contacts || []).map((contact, i) => (
            <div
              key={i}
              style={{
                padding: '20px',
                border: '1px solid #e8e4e0',
                borderRadius: '4px',
              }}
            >
              <h3 style={{ fontSize: '1.2rem', marginBottom: '4px' }}>
                {contact.name}
              </h3>
              <p
                style={{
                  fontSize: '0.9rem',
                  color: '#b8956a',
                  textTransform: 'uppercase',
                  marginBottom: '12px',
                }}
              >
                {contact.title}
              </p>
              <p style={{ margin: '6px 0' }}>
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
              </p>
              <p style={{ margin: '6px 0' }}>
                <a href={`tel:${contact.phone}`}>{contact.phone}</a>
              </p>
            </div>
          ))}
        </div>
      );
      break;

    case 'button':
      content = (
        <button
          style={{
            padding: '14px 28px',
            background: '#1a1410',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontWeight: 'bold',
            cursor: 'pointer',
            margin: '20px 0',
          }}
        >
          {block.label || 'Cliquez ici'}
        </button>
      );
      break;

    case 'image':
      content = (
        <img
          src={block.url || 'https://via.placeholder.com/600x300'}
          alt={block.alt || ''}
          style={{
            width: '100%',
            borderRadius: '4px',
            margin: '20px 0',
            maxHeight: '400px',
            objectFit: 'cover',
          }}
        />
      );
      break;

    case 'divider':
      content = (
        <hr
          style={{
            border: 'none',
            borderTop: '2px solid #e8e4e0',
            margin: '40px 0',
          }}
        />
      );
      break;

    case 'download':
      content = (
        <div
          style={{
            background: '#f5f3f0',
            padding: '30px',
            borderRadius: '4px',
            textAlign: 'center',
            margin: '20px 0',
          }}
        >
          <h3 style={{ marginBottom: '10px' }}>{block.title}</h3>
          <p style={{ marginBottom: '16px' }}>{block.description}</p>
          <button
            style={{
              padding: '12px 24px',
              background: '#1a1410',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            ⬇️ Télécharger
          </button>
        </div>
      );
      break;

    default:
      content = null;
  }

  return (
    <div
      className={`editable-block ${isSelected ? 'selected' : ''}`}
      data-block-id={block.id}
      draggable
      onDragStart={() => onDragStart()}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onClick={() => onSelect()}
    >
      <span className="block-label">{block.type}</span>
      <span className="block-drag-handle">⋮</span>
      <div className="block-actions">
        <button
          className="block-action-btn"
          onClick={(e) => {
            onSelect();
            e.stopPropagation();
          }}
          title="Éditer"
        >
          ✏️
        </button>
        <button
          className="block-action-btn delete"
          onClick={(e) => {
            onDelete();
            e.stopPropagation();
          }}
          title="Supprimer"
        >
          🗑️
        </button>
      </div>
      {content}
    </div>
  );
}

BlockRenderer.propTypes = {
  block: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string.isRequired,
    level: PropTypes.string,
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    items: PropTypes.array,
    contacts: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        title: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
      })
    ),
    label: PropTypes.string,
    url: PropTypes.string,
    alt: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fontFamily: PropTypes.string,
    fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fontStyle: PropTypes.string,
    textDecoration: PropTypes.string,
    textAlign: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onDragOver: PropTypes.func.isRequired,
};

export default BlockRenderer;
